import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { useLocalStorageValue } from '@react-hookz/web';
import {
  Sheet,
  SheetContentResizable,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/spinner';
import { useEmailForLabeling } from '@/api/rpc/hooks/ship-and-debit/useLabeling';
import { EmailQuickPreview } from './EmailQuickPreview';

interface Props {
  inboundEmailId: string | null;
  onClose: () => void;
  /**
   * Offer "Open in review" (#7656): Browse is the record and asks nothing (#7657);
   * every answer, a label included, is given on the review page — for any email,
   * queued or not.
   */
  canReview: boolean;
}

/**
 * Half the viewport, and the reviewer's own number after the first drag.
 *
 * The sheet was fixed at `sm:max-w-2xl` (672px), which is narrower than most of what
 * it has to show: a supplier email's quoted history, an attachment table, and now a
 * list of existing 849s to pick between. Everything wrapped. It is a panel over a
 * list, not a dialog, so the right fix is the one the AR and AP slide-ins already
 * make — drag the edge, and remember where it was left.
 */
const DEFAULT_WIDTH_PERCENT = 50;

/**
 * Browse mode's detail view: the email, read-only. Browse is the record (#7657) —
 * the one place an email is answered is the review page, which "Open in Review"
 * opens it on.
 *
 * A slide-over rather than a route so the list keeps its scroll position and the
 * reviewer can work down it — but the open email is still in the URL (`?emailId=`),
 * so a specific one can be linked to and reopened.
 */
export function LabelingEmailSheet({
  inboundEmailId,
  onClose,
  canReview,
}: Props) {
  const gt = useGT();
  const {
    data: item,
    isLoading,
    isError,
  } = useEmailForLabeling(inboundEmailId);
  // Per-reviewer and per-browser on purpose: how wide this wants to be depends on the
  // screen it is being read on, which is not a fact worth a round trip to store.
  const { value: widthPercent, set: setWidthPercent } =
    useLocalStorageValue<number>('sd-849-labeling-sheet-width', {
      defaultValue: DEFAULT_WIDTH_PERCENT,
    });

  return (
    <Sheet
      open={inboundEmailId != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContentResizable
        resizableProps={{
          defaultSize: widthPercent ?? DEFAULT_WIDTH_PERCENT,
          onResize: setWidthPercent,
          // A floor, not a preference. Dragged to nothing the sheet becomes a sliver
          // with a close button and no way back to a usable width.
          minSize: 25,
          className: 'flex flex-col gap-0',
        }}
      >
        <SheetHeader className="flex-row items-center gap-3 border-b px-5 py-3">
          <SheetTitle className="min-w-0 flex-1 truncate text-base">
            {item?.subject ??
              gt('(no subject)', {
                context: 'labeling email missing subject',
              })}
          </SheetTitle>
          {canReview && inboundEmailId ? (
            <Button variant="outline" size="sm" asChild>
              <Link
                to="/ship-and-debit/849-review"
                search={{ emailId: inboundEmailId }}
              >
                <T context="labeling browse: open this email on the 849 review page">
                  Open in Review
                </T>
              </Link>
            </Button>
          ) : null}
        </SheetHeader>

        {isError ? (
          // Without this branch a failed fetch left the spinner running forever,
          // which reads as a hung app rather than a retryable error.
          <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
            <T context="labeling detail load error">
              This email could not be loaded. Close and try again.
            </T>
          </div>
        ) : isLoading || !item ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-auto p-5">
            <EmailQuickPreview key={item.inboundEmailId} item={item} />
          </div>
        )}
      </SheetContentResizable>
    </Sheet>
  );
}
